import ApperIcon from '@/components/ApperIcon';
import { cn } from '@/lib/utils';
import { ARC_FAMILY } from '@/canvas/spine';
import { TAG_LABEL } from '@/canvas/helpers';

// The three arcs of the course, each with its own colour family.
export const FAMILY = {
  cyan: {
    solid: 'bg-(color:--arc-case)',
    soft: 'bg-(color:--arc-case-soft)',
    ink: 'text-(color:--arc-case-ink)',
    text: 'text-(color:--arc-case)',
    edge: 'border-(color:--arc-case)',
    edgeLeft: 'border-l-(color:--arc-case)',
    edgeTop: 'border-t-(color:--arc-case)',
  },
  purple: {
    solid: 'bg-(color:--arc-build)',
    soft: 'bg-(color:--arc-build-soft)',
    ink: 'text-(color:--arc-build-ink)',
    text: 'text-(color:--arc-build)',
    edge: 'border-(color:--arc-build)',
    edgeLeft: 'border-l-(color:--arc-build)',
    edgeTop: 'border-t-(color:--arc-build)',
  },
  green: {
    solid: 'bg-(color:--arc-happen)',
    soft: 'bg-(color:--arc-happen-soft)',
    ink: 'text-(color:--arc-happen-ink)',
    text: 'text-(color:--arc-happen)',
    edge: 'border-(color:--arc-happen)',
    edgeLeft: 'border-l-(color:--arc-happen)',
    edgeTop: 'border-t-(color:--arc-happen)',
  },
};

export const familyOf = (def) => FAMILY[ARC_FAMILY[def?.arc]] || FAMILY.cyan;

// Step colours, in the order of the printed canvases: cyan, teal, green, purple, amber, blue.
export const STEP_SOLID = [
  'bg-(color:--step-1)',
  'bg-(color:--step-2)',
  'bg-(color:--step-3)',
  'bg-(color:--step-4)',
  'bg-(color:--step-5)',
  'bg-(color:--step-6)',
];
export const stepSolid = (i) => STEP_SOLID[i % STEP_SOLID.length];
export const stepText = (i) => (i % STEP_SOLID.length === 4 ? 'text-highlight-foreground' : 'text-white');

const STATUS = {
  'Not started': { icon: 'Circle', cls: 'bg-muted text-muted-foreground border border-transparent' },
  'In progress': { icon: 'Pencil', cls: 'bg-warning-muted text-warning border border-warning-border' },
  Confirmed: { icon: 'CircleCheck', cls: 'bg-success-muted text-success border border-success-border' },
  Review: { icon: 'TriangleAlert', cls: 'bg-highlight text-highlight-foreground border border-transparent' },
};

export function statusKey(state) {
  if (!state) return 'Not started';
  if (state.stale) return 'Review';
  if (state.confirmed) return 'Confirmed';
  return state.started ? 'In progress' : 'Not started';
}

export function StatusBadge({ state, className }) {
  const key = statusKey(state);
  const s = STATUS[key];
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap', s.cls, className)}>
      <ApperIcon name={s.icon} size={12} />
      {key}
    </span>
  );
}

const TAG_CLS = {
  M: 'bg-(color:--tag-m-soft) text-(color:--tag-m)',
  E: 'bg-(color:--tag-e-soft) text-(color:--tag-e)',
  G: 'bg-(color:--tag-g-soft) text-(color:--tag-g)',
};

export function TagChip({ tag, withLabel = false, className }) {
  const t = String(tag || '').toUpperCase();
  if (!TAG_CLS[t]) return null;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-semibold', TAG_CLS[t], className)}>
      {t}
      {withLabel && <span className="font-medium">{TAG_LABEL[t]}</span>}
    </span>
  );
}

export function ProgressBar({ value = 0, className, barClassName, label }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label={label || 'Progress'}
      className={cn('h-1.5 w-full overflow-hidden rounded-full bg-muted', className)}
    >
      <div className={cn('h-full rounded-full bg-primary transition-[width] duration-(--transition-slow)', barClassName)} style={{ width: `${pct}%` }} />
    </div>
  );
}

// the number of a canvas in its arc colour; the Case Card shows a compass
export function CanvasNumber({ def, size = 'md', className }) {
  const fam = familyOf(def);
  const dim = size === 'lg' ? 'size-12 text-xl' : size === 'sm' ? 'size-7 text-xs' : 'size-9 text-base';
  return (
    <span className={cn('inline-flex shrink-0 items-center justify-center rounded-xl font-heading font-semibold text-white', dim, fam.solid, className)}>
      {def.no === 0 ? <ApperIcon name="IdCard" size={size === 'lg' ? 22 : size === 'sm' ? 14 : 18} /> : def.no}
    </span>
  );
}

export const canvasLabel = (def) => (def.no === 0 ? 'Case Card' : `Canvas ${def.no}`);

export function minutesLabel(def) {
  const m = def?.minutes;
  if (m === undefined || m === null || m === '') return '';
  return typeof m === 'number' ? `${m} min` : String(m);
}
